/**
 * Composant pour sélectionner la localisation
 * - Pour le Sénégal : Région → Département → Commune (hiérarchique)
 * - Pour les autres pays : État/Province → Ville → Code Postal (texte libre)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as localitesService from '../../services/localites.service.js';
import CountrySelector from './CountrySelector.jsx';
import InternationalAddressInput from './InternationalAddressInput.jsx';
import './LocaliteSelector.css';

function LocaliteSelector({ 
  country,
  countryCode,
  regionId, 
  departmentId, 
  communeId, 
  quartier,
  // Champs pour adresses internationales
  state,
  city,
  postal_code,
  address_line,
  onChange,
  errors = {},
  required = true,
  // Masque le sélecteur de pays et force le Sénégal (relais = Sénégal uniquement)
  showCountrySelector = true,
  // Masque le champ quartier/village (optionnel selon le contexte)
  showQuartier = true,
  // Quartier/village obligatoire (Relais Public, invitations admin)
  requireQuartier = false,
}) {
  // Déterminer si c'est le Sénégal (plus robuste)
  const checkIsSenegal = () => {
    const countryMatch = country === 'Sénégal' || country === 'Senegal';
    const codeMatch = countryCode === 'SN';
    return countryMatch || codeMatch;
  };
  
  const isSenegal = checkIsSenegal();
  
  const [regions, setRegions] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [quartiers, setQuartiers] = useState([]);
  const [loading, setLoading] = useState({
    regions: false,
    departements: false,
    communes: false,
    quartiers: false,
  });

  // Utiliser une ref pour tracker si les régions ont été chargées pour éviter les boucles infinies
  const regionsLoadedRef = useRef(false);
  const currentCountryRef = useRef(null); // Initialiser à null pour détecter le montage initial
  const loadingInProgressRef = useRef(false); // Pour éviter les appels multiples
  
  // Log des valeurs initiales
  console.log('🏗️ [LocaliteSelector] Montage/Re-render', {
    country,
    countryCode,
    isSenegal,
    regionsCount: regions.length,
    regionsLoaded: regionsLoadedRef.current
  });

  // Définir loadRegions avec useCallback pour stabiliser la référence
  const loadRegions = useCallback(async () => {
    // Éviter les appels multiples
    if (loadingInProgressRef.current) {
      console.log('⏸️ [loadRegions] Chargement déjà en cours, on ignore cet appel');
      return;
    }
    
    try {
      loadingInProgressRef.current = true;
      console.log('🔄 [loadRegions] Démarrage du chargement des régions...');
      setLoading(prev => ({ ...prev, regions: true }));
      
      console.log('🔄 [loadRegions] Appel de localitesService.getRegions()...');
      const response = await localitesService.getRegions();
      console.log('✅ [loadRegions] Réponse API complète:', JSON.stringify(response, null, 2));
      console.log('✅ [loadRegions] Type de réponse:', typeof response);
      console.log('✅ [loadRegions] response.success:', response?.success);
      console.log('✅ [loadRegions] response.data:', response?.data);
      console.log('✅ [loadRegions] Array.isArray(response.data):', Array.isArray(response?.data));
      
      // Le service retourne directement response.data de l'API axios
      // Donc response devrait être {success: true, data: [...]}
      if (response && response.success && Array.isArray(response.data)) {
        const regionsData = response.data;
        console.log(`✅ [loadRegions] ${regionsData.length} régions reçues`);
        if (regionsData.length > 0) {
          console.log('✅ [loadRegions] Première région (échantillon):', regionsData[0]);
        }
        
        if (regionsData.length > 0) {
          console.log('✅ [loadRegions] Données valides, mise à jour du state...');
          setRegions(regionsData);
          regionsLoadedRef.current = true;
          console.log('✅✅✅ [loadRegions] RÉGIONS MISES À JOUR AVEC SUCCÈS ! ✅✅✅');
        } else {
          console.warn('⚠️ [loadRegions] response.data est un tableau vide');
          setRegions([]);
          regionsLoadedRef.current = false;
        }
      } else {
        console.error('❌ [loadRegions] Réponse API invalide:', response);
        console.error('❌ [loadRegions] Structure attendue: {success: true, data: [...]}');
        setRegions([]);
        regionsLoadedRef.current = false;
      }
    } catch (error) {
      console.error('❌ [loadRegions] Erreur lors du chargement des régions:', error);
      console.error('❌ [loadRegions] Message:', error.message);
      console.error('❌ [loadRegions] Stack:', error.stack);
      setRegions([]);
      regionsLoadedRef.current = false;
    } finally {
      loadingInProgressRef.current = false;
      setLoading(prev => ({ ...prev, regions: false }));
      console.log('✅ [loadRegions] Chargement terminé, loadingInProgressRef mis à false');
    }
  }, []); // Pas de dépendances car la fonction est stable

  // Charger les régions au montage et quand le pays change (si Sénégal)
  useEffect(() => {
    const currentCountry = countryCode || country || '';
    const checkIsSenegalValue = country === 'Sénégal' || country === 'Senegal' || countryCode === 'SN';
    const isInitialMount = currentCountryRef.current === null;
    const countryChanged = currentCountryRef.current !== currentCountry && currentCountryRef.current !== null;
    
    console.log('🌍 [useEffect] Déclenché', { 
      country, 
      countryCode, 
      currentCountry,
      checkIsSenegalValue,
      isInitialMount,
      countryChanged,
      regionsLoaded: regionsLoadedRef.current,
      loadingInProgress: loadingInProgressRef.current,
      regionsCount: regions.length
    });
    
    // Si ce n'est pas le Sénégal, réinitialiser
    if (!checkIsSenegalValue) {
      console.log('🔄 [useEffect] Ce n\'est pas le Sénégal, réinitialisation...');
      regionsLoadedRef.current = false;
      loadingInProgressRef.current = false;
      setRegions([]);
      setDepartements([]);
      setCommunes([]);
      setLoading({
        regions: false,
        departements: false,
        communes: false,
      });
      currentCountryRef.current = currentCountry;
      return;
    }
    
    // Si c'est le Sénégal
    // Mettre à jour la ref du pays actuel
    currentCountryRef.current = currentCountry;
    
    // Si c'est le montage initial OU si le pays a changé, reset les flags
    if (isInitialMount || countryChanged) {
      console.log('🔄 [useEffect] Montage initial ou pays changé, reset des flags');
      regionsLoadedRef.current = false;
      loadingInProgressRef.current = false;
    }
    
    // Charger les régions si elles n'ont pas encore été chargées et qu'on n'est pas en train de charger
    if (!regionsLoadedRef.current && !loadingInProgressRef.current) {
      console.log('📥 [useEffect] ⭐ APPEL DE loadRegions() ⭐');
      loadRegions();
    } else {
      console.log('⏭️ [useEffect] Pas de chargement nécessaire', {
        regionsLoaded: regionsLoadedRef.current,
        loadingInProgress: loadingInProgressRef.current
      });
    }
    // `regions.length` intentionally omitted: we only re-load regions when the
    // country (id or code) changes, not on every internal regions list update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, countryCode, loadRegions]); // Inclure loadRegions dans les dépendances

  const loadDepartements = async (regionId) => {
    try {
      setLoading(prev => ({ ...prev, departements: true }));
      const response = await localitesService.getDepartementsByRegion(regionId);
      if (response.success) {
        setDepartements(response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
      setDepartements([]);
    } finally {
      setLoading(prev => ({ ...prev, departements: false }));
    }
  };

  // Charger les départements quand la région change (seulement si Sénégal)
  useEffect(() => {
    if (isSenegal && regionId) {
      loadDepartements(regionId);
    } else {
      setDepartements([]);
      setCommunes([]);
      setQuartiers([]); // Clear quartiers when region changes
      if (isSenegal) {
        onChange({ department_id: null, commune_id: null, quartier: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionId, isSenegal]);

  const loadCommunes = async (departmentId) => {
    try {
      setLoading(prev => ({ ...prev, communes: true }));
      const response = await localitesService.getCommunesByDepartement(departmentId);
      if (response.success) {
        setCommunes(response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des communes:', error);
      setCommunes([]);
    } finally {
      setLoading(prev => ({ ...prev, communes: false }));
    }
  };

  // Charger les communes quand le département change (seulement si Sénégal)
  useEffect(() => {
    if (isSenegal && departmentId) {
      loadCommunes(departmentId);
    } else {
      setCommunes([]);
      setQuartiers([]); // Clear quartiers when department changes
      if (isSenegal) {
        onChange({ commune_id: null, quartier: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, isSenegal]);

  const handleRegionChange = (e) => {
    const newRegionId = e.target.value || null;
    setQuartiers([]); // Clear quartiers when region changes
    onChange({ 
      region_id: newRegionId, 
      department_id: null, 
      commune_id: null,
      quartier: null
    });
  };

  const handleDepartmentChange = (e) => {
    const newDepartmentId = e.target.value || null;
    setQuartiers([]); // Clear quartiers when department changes
    onChange({ 
      department_id: newDepartmentId, 
      commune_id: null,
      quartier: null
    });
  };

  const handleCommuneChange = (e) => {
    const newCommuneId = e.target.value || null;
    onChange({ commune_id: newCommuneId, quartier: null }); // Reset quartier when commune changes
  };

  // Load quartiers when commune changes
  useEffect(() => {
    if (isSenegal && communeId) {
      const loadQuartiers = async () => {
        try {
          setLoading(prev => ({ ...prev, quartiers: true }));
          const response = await localitesService.getQuartiersByCommune(communeId);
          if (response.success && Array.isArray(response.data)) {
            setQuartiers(response.data);
          } else {
            setQuartiers([]);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des quartiers:', error);
          setQuartiers([]);
        } finally {
          setLoading(prev => ({ ...prev, quartiers: false }));
        }
      };
      loadQuartiers();
    } else {
      setQuartiers([]);
    }
  }, [communeId, isSenegal]);

  const handleQuartierChange = (e) => {
    const selectedValue = e.target.value;
    // If empty or "none", set to null, otherwise use the value (ID from dropdown or text from input)
    onChange({ quartier: selectedValue === '' || selectedValue === 'none' ? null : selectedValue });
  };

  const handleCountryChange = (countryData) => {
    if (onChange) {
      const isNewCountrySenegal = countryData.country === 'Sénégal' || countryData.country === 'Senegal' || countryData.country_code === 'SN';
      
      // Si on change de pays, réinitialiser les champs d'adresse
      if (!isNewCountrySenegal && isSenegal) {
        // On passe de Sénégal à un autre pays
        onChange({
          country: countryData.country,
          country_code: countryData.country_code,
          phone_code: countryData.phone_code,
          // Réinitialiser les champs sénégalais
          region_id: null,
          department_id: null,
          commune_id: null,
          quartier: null,
          // Initialiser les champs internationaux vides
          state: '',
          city: '',
          postal_code: '',
          address_line: '',
        });
      } else if (isNewCountrySenegal && !isSenegal) {
        // On passe d'un autre pays au Sénégal
        onChange({
          country: countryData.country,
          country_code: countryData.country_code,
          phone_code: countryData.phone_code,
          // Réinitialiser les champs internationaux
          state: null,
          city: null,
          postal_code: null,
          address_line: null,
          // Initialiser les champs sénégalais vides
          region_id: null,
          department_id: null,
          commune_id: null,
          quartier: '',
        });
      } else {
        // Même type de pays, juste mettre à jour les infos du pays
        onChange({
          country: countryData.country,
          country_code: countryData.country_code,
          phone_code: countryData.phone_code,
        });
      }
    }
  };

  return (
    <div className="localite-selector">
      {/* Sélecteur de pays (masqué quand la localisation est verrouillée sur le Sénégal) */}
      {showCountrySelector && (
        <CountrySelector
          value={countryCode || country}
          onChange={handleCountryChange}
          errors={errors}
          required={required}
        />
      )}

      {/* Afficher le formulaire adapté selon le pays */}
      {isSenegal ? (
        <>
          {/* Formulaire Sénégal : Région → Département → Commune */}
          <div className="form-group">
            <label htmlFor="region_id" className="sr-only">Région {required && <span className="required">*</span>}</label>
            <select
              id="region_id"
              name="region_id"
              value={regionId || ''}
              onChange={handleRegionChange}
              className={`form-control select-with-placeholder ${errors.region_id ? 'error' : ''}`}
              required={required && isSenegal}
              disabled={loading.regions}
            >
              <option value="" disabled hidden>Région {required && '*'}</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.nom}
                </option>
              ))}
            </select>
            {errors.region_id && <div className="field-error">{errors.region_id}</div>}
            {loading.regions && <small className="form-text">Chargement...</small>}
          </div>

          <div className="form-group">
            <label htmlFor="department_id" className="sr-only">Département {required && <span className="required">*</span>}</label>
            <select
              id="department_id"
              name="department_id"
              value={departmentId || ''}
              onChange={handleDepartmentChange}
              className={`form-control select-with-placeholder ${errors.department_id ? 'error' : ''}`}
              required={required && isSenegal}
              disabled={!regionId || loading.departements}
            >
              <option value="" disabled hidden>{regionId ? 'Département *' : 'Sélectionnez d\'abord une région'}</option>
              {departements.map((departement) => (
                <option key={departement.id} value={departement.id}>
                  {departement.nom}
                </option>
              ))}
            </select>
            {errors.department_id && <div className="field-error">{errors.department_id}</div>}
            {loading.departements && <small className="form-text">Chargement...</small>}
          </div>

          <div className="form-group">
            <label htmlFor="commune_id" className="sr-only">Commune {required && <span className="required">*</span>}</label>
            <select
              id="commune_id"
              name="commune_id"
              value={communeId || ''}
              onChange={handleCommuneChange}
              className={`form-control select-with-placeholder ${errors.commune_id ? 'error' : ''}`}
              required={required && isSenegal}
              disabled={!departmentId || loading.communes}
            >
              <option value="" disabled hidden>{departmentId ? 'Commune *' : 'Sélectionnez d\'abord un département'}</option>
              {communes.map((commune) => (
                <option key={commune.id} value={commune.id}>
                  {commune.nom}
                </option>
              ))}
            </select>
            {errors.commune_id && <div className="field-error">{errors.commune_id}</div>}
            {loading.communes && <small className="form-text">Chargement...</small>}
          </div>

          {showQuartier && (
          <div className="form-group">
            <label htmlFor="quartier" className="sr-only">
              Quartier/Village {requireQuartier && <span className="required">*</span>}
            </label>
            {communeId && quartiers.length > 0 ? (
              // Show dropdown if quartiers are available for the selected commune
              <select
                id="quartier"
                name="quartier"
                value={quartier || ''}
                onChange={handleQuartierChange}
                className={`form-control ${errors.quartier ? 'error' : ''}`}
                disabled={loading.quartiers || !communeId}
                required={requireQuartier && isSenegal}
                aria-required={requireQuartier ? 'true' : undefined}
              >
                <option value="" disabled hidden>
                  {requireQuartier ? 'Quartier / village *' : '-- Aucun quartier/village (optionnel) --'}
                </option>
                {quartiers.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.nom}
                  </option>
                ))}
              </select>
            ) : (
              // Show text input if no quartiers available or commune not selected
              <input
                type="text"
                id="quartier"
                name="quartier"
                value={quartier || ''}
                onChange={handleQuartierChange}
                className={`form-control ${errors.quartier ? 'error' : ''}`}
                placeholder={
                  requireQuartier
                    ? 'Quartier ou village *'
                    : communeId
                      ? 'Quartier/Village (optionnel)'
                      : 'Quartier/Village (optionnel)'
                }
                maxLength={200}
                disabled={loading.quartiers}
                required={requireQuartier && isSenegal}
                aria-required={requireQuartier ? 'true' : undefined}
              />
            )}
            {loading.quartiers && <small className="form-text">Chargement des quartiers...</small>}
            {errors.quartier && <div className="field-error">{errors.quartier}</div>}
            <small className="form-text">
              {requireQuartier
                ? 'Obligatoire — permet aux bénéficiaires de trouver le Relais le plus proche.'
                : communeId && quartiers.length > 0
                  ? 'Optionnel - Sélectionnez un quartier/village de la liste ou laissez vide'
                  : 'Optionnel - Nom du quartier, village ou hameau'}
            </small>
          </div>
          )}

          {/* Adresse libre (Sénégal) — précise la localisation au-delà du quartier */}
          <div className="form-group">
            <label htmlFor="address_line" className="sr-only">Adresse</label>
            <input
              type="text"
              id="address_line"
              name="address_line"
              value={address_line || ''}
              onChange={(e) => onChange({ address_line: e.target.value })}
              className="form-control"
              placeholder="Adresse (rue, n°, point de repère…) — optionnel"
              maxLength={200}
            />
            <small className="form-text">
              Optionnel — précisez votre adresse ou un point de repère pour faciliter la localisation.
            </small>
          </div>
        </>
      ) : (
        /* Formulaire International : État → Ville → Code Postal */
        <InternationalAddressInput
          state={state}
          city={city}
          postalCode={postal_code}
          addressLine={address_line}
          quartier={quartier}
          onChange={onChange}
          errors={errors}
          required={required}
        />
      )}
    </div>
  );
}

export default LocaliteSelector;
